import React, { useEffect, useRef, useState } from 'react'
import { TXT_SHORT_NAME } from './txt-record'

// "Check now" on a domain (owner 2026-10-23, after a real verify went
// instantly green and looked like nothing happened): while auth looks the
// record up the button says "Checking…", held at least CHECK_MIN_MS so it
// never flickers (no fake progress), then a line says what was found, in
// words, from auth's answer:
//   check "found"       -> Found it: <domain> is verified
//   check "not_found"   -> Not found yet at _edxr-verify.<domain>. ...
//   check "unreachable" -> Couldn't reach DNS just now. ...
//   409 organization_domain.txt_name_doubled -> auth's own sentence
//   429 organization_domain.check_cooldown   -> "Check again in Ns", counting down
// The organization's Domains uses it whole; staff's organization SSO tab and
// domains list show the same words (checkLine, auth's sentences) in their
// own toast or status line.

export const CHECK_MIN_MS = 400
export const DOUBLED_CODE = 'organization_domain.txt_name_doubled'
export const COOLDOWN_CODE = 'organization_domain.check_cooldown'

const bodyOf = err => (err && err.data && typeof err.data === 'object' ? err.data : {})

const wait = ms => new Promise(resolve => setTimeout(resolve, ms))

// The line for an answer: { tone: 'ok' | 'warn' | 'bad', text }, or null.
export const checkLine = (domain, result) => {
  if (!result) return null
  if (result.check === 'found' || (!result.check && result.state === 'verified' && result.lastOkAt)) {
    return { tone: 'ok', text: `Found it: ${domain} is verified.` }
  }
  if (result.check === 'unreachable') return { tone: 'warn', text: "Couldn't reach DNS just now. Try again in a moment." }
  return { tone: 'warn', text: `Not found yet at ${TXT_SHORT_NAME}.${domain}. New records can take a few minutes to appear. We also check automatically.` }
}

// run: the call that checks (resolves to auth's answer, throws its refusal).
// onChecked: after a check that answered (reload the list).
export const useDomainCheck = ({ domain, run, onChecked, failure = "The domain couldn't be checked right now." }) => {
  const [checking, setChecking] = useState(false)
  const [line, setLine] = useState(null)
  const [error, setError] = useState(null)
  const [cooldown, setCooldown] = useState(0)
  const live = useRef(true)

  useEffect(() => () => { live.current = false }, [])

  useEffect(() => {
    if (cooldown <= 0) return undefined
    const timer = setTimeout(() => { if (live.current) setCooldown(c => c - 1) }, 1000)
    return () => clearTimeout(timer)
  }, [cooldown])

  const check = async () => {
    if (checking || cooldown > 0) return
    setChecking(true)
    setLine(null)
    setError(null)
    const started = Date.now()
    let result
    let refused
    try {
      result = await run()
    } catch (err) {
      refused = err || {}
    }
    const elapsed = Date.now() - started
    if (elapsed < CHECK_MIN_MS) await wait(CHECK_MIN_MS - elapsed)
    if (!live.current) return
    setChecking(false)

    if (refused) {
      const body = bodyOf(refused)
      if (refused.status === 429 && body.code === COOLDOWN_CODE) {
        const seconds = Number(body.retryAfter) || 10
        setCooldown(seconds)
        return
      }
      if (body.code === DOUBLED_CODE && body.message) {
        setLine({ tone: 'warn', text: body.message })
        return
      }
      setError({ text: (body.message && (refused.status === 400 || refused.status === 403 || refused.status === 404 || refused.status === 409) ? body.message : failure), failure: refused })
      return
    }

    setLine(checkLine(domain, result))
    try {
      onChecked && (await onChecked(result))
    } catch (err) {
      if (live.current) setError({ text: "The check finished, but the list couldn't be refreshed.", failure: err })
    }
  }

  const label = checking ? 'Checking…' : cooldown > 0 ? `Check again in ${cooldown}s` : 'Check now'

  return { check, checking, cooldown, label, disabled: checking || cooldown > 0, line, error }
}

// The line under a domain's row: a live region that stays mounted, so a
// screen reader hears each result as it lands (and the countdown is never
// read out second by second).
export const DomainCheckLine = ({ line }) =>
  <div role="status" aria-live="polite">
    {line && <p className={`small m-0 ${line.tone === 'ok' ? 'text-success' : 'sw-warn'}`} data-testid="check-line">{line.text}</p>}
  </div>
